/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import RESOURCE from './ResourceType';
import LaunchType from './LaunchType';
import { IResourceId, IResourceProps, IResourceMap } from './ResourceLoader';
import AppTunnelModel from './appTunnel/Model';
import NetworkAccessModel from './networkAccess/Model';
import PortalAccessModel from './portalAccess/Model';
import RemoteDesktopModel from './remoteDesktop/Model';
import createRemoteDesktopResource from './remoteDesktop/CreateRemoteDesktopResource';
import WebtopFolderModel from './webtopFolder/Model';
import WebtopLinkModel from './webtopLink/Model';
import OauthClientAppModel from './oauthClientApp/Model';
import OauthScopeModel from './oauthScope/Model';
import WebtopSectionModel from './webtopSection/Model';
import SamlModel from './saml/Model';

/*
TODO: Put everything from put resources here
*/

type IWebtopResourceModel =
    | AppTunnelModel
    | NetworkAccessModel
    | PortalAccessModel
    | RemoteDesktopModel
    | SamlModel
    | WebtopFolderModel
    | WebtopLinkModel;

type IResourceModel =
    // Webtop Resources
    | IWebtopResourceModel
    | WebtopSectionModel
    // Oauth resources
    | OauthClientAppModel
    | OauthScopeModel;

interface IResourceLaunchEventDetail {
    resource: IResourceModel;
    launchType?: LaunchType;
}

/**
 * Request to add specified resources into store, creating them from props when necessary.
 */
interface IResourceConfigurationAdd {
    method?: 'add';
    /**
     * Map with resource initialization data.
     */
    resourceMap: IResourceMap;
}

/**
 * Request to replace `template` resource with specified list of resources in all sections that have `template`.
 */
interface IResourceConfigurationReplace {
    method: 'replace';
    /**
     * Map with resource initialization data.
     */
    resourceMap: IResourceMap;
    /**
     * Value that specifies section list and positions.
     */
    replace: IResourceId;
}

/**
 * Request to assign resources to sections.
 *
 * `sections` -- optional value that specifies a list of sections that should contain the resource.
 *
 * When no sections or template specified, resources are assigned to default sections.
 */
interface IResourceConfigurationAssign {
    method: 'assign';
    resourceIDs: IResourceId[];
    /**
     * Optional value that specifies a list of sections that should contain the resource.
     */
    sections?: string[];
}

/**
 * Request to remove resources from sections.
 *
 * `sections` -- optional value that specifies a list of sections that should no longer contain the resource.
 *
 * When no sections specified, resources are removed from all sections.
 */
interface IResourceConfigurationRemove {
    method: 'remove';
    resourceIDs: IResourceId[];
    /**
     * Optional value that specifies a list of sections that should no longer contain the resource.
     */
    sections?: string[];
}
/**
 * Resource store update specification.
 *
 * `method` - `add` | `replace` | `assign` | `remove`
 *  * `add` creates resources specified by `resourceMap` and adds them to the configuration
 *  * `replace` creates resources specified with `resourceMap` and inserts them to the configuration
 *    at the same position and sections as the resource specified by `replace` field
 *  * `assign` assigns resources specified by `resourceIDs` to the sections specified by `sections`
 *    or default sections
 *  * `remove` removes resources specified by `resourceIDs` from the sections specified by `sections`
 *    or from all sections
 *
 * `resourceMap` - field for `add` or `replace` requests with map of new resource initialization data
 * `replace` - field for `replace` request with the ID of resource to inherit positions and sections
 * `resourceIDs` - field for `assign` or `remove` requests with list of resource IDs to operate on
 * `sections` - field for `assign` or `remove` requests with list of string section IDs to operate on
 */
type IResourceConfigurationEventDetail =
    | IResourceConfigurationAdd
    | IResourceConfigurationAssign
    | IResourceConfigurationRemove
    | IResourceConfigurationReplace;

/**
 * IResourceStore structure contains all resources loaded to the page.
 */
interface IResourceStore {
    // Webtop Resources
    [RESOURCE.APP_TUNNEL]?: Record<string, AppTunnelModel>;
    [RESOURCE.NETWORK_ACCESS]?: Record<string, NetworkAccessModel>;
    [RESOURCE.PORTAL_ACCESS]?: Record<string, PortalAccessModel>;
    [RESOURCE.REMOTE_DESKTOP]?: Record<string, RemoteDesktopModel>;
    [RESOURCE.SAML]?: Record<string, SamlModel>;
    [RESOURCE.WEBTOP_FOLDER]?: Record<string, WebtopFolderModel>;
    [RESOURCE.WEBTOP_LINK]?: Record<string, WebtopLinkModel>;
    [RESOURCE.WEBTOP_SECTION]?: Record<string, WebtopSectionModel>;
    // Ouath Resources
    [RESOURCE.OAUTH_CLIENT_APP]?: Record<string, OauthClientAppModel>;
    [RESOURCE.OAUTH_SCOPE]?: Record<string, OauthScopeModel>;
}

/* TODO: Comments */
function createResource(id: string, resource: IResourceProps): IResourceModel | undefined {
    if (!resource || !resource.props || !resource.props.type) {
        return;
    }
    switch (resource.props.type) {
        // Webtop Resources
        case RESOURCE.APP_TUNNEL:
            return new AppTunnelModel(id, resource);
        case RESOURCE.NETWORK_ACCESS:
            return new NetworkAccessModel(id, resource);
        case RESOURCE.REMOTE_DESKTOP:
            return createRemoteDesktopResource(id, resource);
        case RESOURCE.PORTAL_ACCESS:
            return new PortalAccessModel(id, resource);
        case RESOURCE.SAML:
            return new SamlModel(id, resource);
        case RESOURCE.WEBTOP_FOLDER:
            return new WebtopFolderModel(id, resource);
        case RESOURCE.WEBTOP_LINK:
            return new WebtopLinkModel(id, resource);
        case RESOURCE.WEBTOP_SECTION:
            return new WebtopSectionModel(id, resource);
        // Oauth Resources
        case RESOURCE.OAUTH_CLIENT_APP:
            return new OauthClientAppModel(id, resource);
        case RESOURCE.OAUTH_SCOPE:
            return new OauthScopeModel(id, resource);
    }
    return;
}

export {
    createResource as default,
    createResource,
    IResourceId,
    IResourceConfigurationEventDetail,
    IResourceLaunchEventDetail,
    IResourceMap,
    IResourceModel,
    IResourceProps,
    IResourceStore,
    IWebtopResourceModel,
};
